// matches the API's limit on submissions released or requoted per request
export const MAX_RELEASES_PER_REQUEST = 500;

export const RELEASE_STATUS_FILTERS = [
    { value: 'Unreleased', label: 'All unreleased (and skipped)' },
    { value: 'ReadyToRelease', label: 'Quoted, waiting for release' },
    { value: 'Quoting', label: 'Quoting' },
    { value: 'QuoteStuck', label: 'Quote stuck' },
    { value: 'NoQuote', label: 'No quote' },
    { value: 'NotFinished', label: 'Not finished' },
    { value: 'PolicyRefused', label: 'Policy cancelled or expired since remarket' },
    { value: 'Skipped', label: 'Skipped (cancelled or lapsed)' },
    { value: 'Released', label: 'Released' },
    { value: 'All', label: 'All' }
];

// how one sales channel the remarket was quoted on answered; no Status means it hasn't answered yet
export interface ChannelQuote {
    SalesChannel: string;
    Status?: 'Ok' | 'Error' | 'NotAvailable' | 'NotSubmitted' | null;
    Total?: number | null;
    DeclineType?: string | null;
    ErrorText?: string | null;
    DebugText?: string | null;
    // why a priced quote can't be bound, so the remarket can't be released on it: Indication, Pending or Expired
    NotBindable?: string | null;
}

export interface RemarketSearchRow {
    // none for a policy that was skipped rather than remarketed
    RemarketSubmissionId?: string | null;
    SourceSubmissionId: string;
    SourceAccountId: string;
    SourcePolicyId: string;
    SourcePolicyNumber?: string | null;
    SourcePolicyAmlinkUrl?: string | null;
    InsuredName?: string | null;
    RiskAddress?: string | null;
    EffectiveDate?: string | null;
    ExpirationDate?: string | null;
    AgentName?: string | null;
    AgentEmail?: string | null;
    AllowedSalesChannels: string[];
    // when it was remarketed, or skipped
    RemarketedAt: string;
    QuoteRequestedAt?: string | null;
    // the lowest priced quote, when one came back
    QuoteSalesChannel?: string | null;
    QuoteTotal?: number | null;
    ChannelQuotes?: ChannelQuote[];
    Status: 'Unreleased' | 'Released' | 'Voided' | 'NotFinished' | 'Quoting' | 'QuoteStuck' | 'NoQuote' | 'SkippedCancelled' | 'SkippedLapsed'
        | 'PolicyCancelled' | 'PolicyLapsed';
    ReleasedAt?: string | null;
    ReleasedBy?: string | null;
    // when the agent edited and requoted it, which made it new business
    VoidedAt?: string | null;
    VoidedBy?: string | null;
    // what the Vave policy's current term costs, totalled like the quotes
    SourceTermTotal?: number | null;
}

const STATUS_LABELS: Record<RemarketSearchRow['Status'], string> = {
    Unreleased: 'Quoted, waiting for release',
    Released: 'Released',
    Voided: 'Released, then requoted by the agent as new business',
    NotFinished: 'Not finished (reprocess first)',
    Quoting: 'Quoting',
    QuoteStuck: 'Quote stuck (requote)',
    NoQuote: 'No quote (requote)',
    SkippedCancelled: 'Skipped: policy cancelled',
    SkippedLapsed: 'Skipped: policy lapsed',
    PolicyCancelled: 'Policy cancelled since remarket (requote if reinstated)',
    PolicyLapsed: 'Policy expired before release'
};

export const releaseStatusLabel = (row: RemarketSearchRow): string => STATUS_LABELS[row.Status] ?? row.Status;

// A skipped policy has no remarketed submission, so it's keyed by the submission it would have been remarketed from.
export const rowKey = (row: RemarketSearchRow): string => row.RemarketSubmissionId ?? `skipped:${row.SourceSubmissionId}`;

// Only a remarket that came back with a priced quote can be released.
export const canRelease = (row: RemarketSearchRow): boolean => row.Status === 'Unreleased';

// One whose quoting is stuck, or that came back without a price, can be sent for quoting again; so can one whose policy was
// cancelled, in case it's been reinstated (the requote checks). An expired policy stays expired, so there's no point.
export const canRequote = (row: RemarketSearchRow): boolean =>
    row.Status === 'QuoteStuck' || row.Status === 'NoQuote' || row.Status === 'PolicyCancelled';

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export const quoteLabel = (row: RemarketSearchRow): string =>
    row.QuoteTotal != null ? `${money.format(row.QuoteTotal)} (${row.QuoteSalesChannel})` : '';

export const sourceTermLabel = (row: RemarketSearchRow): string =>
    row.SourceTermTotal != null ? money.format(row.SourceTermTotal) : '';

const CHANNEL_STATUS_LABELS: Record<string, string> = {
    Ok: 'Quoted',
    Error: 'Error',
    NotAvailable: 'Not available',
    NotSubmitted: 'Waiting'
};

// a priced quote the agent couldn't bind doesn't make the remarket releasable
const NOT_BINDABLE_LABELS: Record<string, string> = {
    Indication: 'indication only',
    Pending: 'pending',
    Expired: 'expired'
};

// e.g. "Munich: $1,234.50", "Munich: $1,234.50 (indication only)", "Digital: Not available", "VavePersonalLines: Waiting"
export const channelQuoteLabel = (quote: ChannelQuote): string => {
    if (!quote.Status) return `${quote.SalesChannel}: Waiting`;
    if (quote.Status === 'Ok' && quote.Total != null && quote.NotBindable) {
        return `${quote.SalesChannel}: ${money.format(quote.Total)} (${NOT_BINDABLE_LABELS[quote.NotBindable] ?? quote.NotBindable})`;
    }
    if (quote.Status === 'Ok' && quote.Total != null) return `${quote.SalesChannel}: ${money.format(quote.Total)}`;
    if (quote.Status === 'Ok') return `${quote.SalesChannel}: Quoted without a price`;
    return `${quote.SalesChannel}: ${CHANNEL_STATUS_LABELS[quote.Status] ?? quote.Status}`;
};

// what the channel said about why it didn't quote, for the tooltip; empty when it said nothing
export const channelQuoteDetails = (quote: ChannelQuote): string[] => [
    quote.DeclineType ? `Decline type: ${quote.DeclineType.replace(/_/g, ' ')}` : null,
    quote.ErrorText ? `Error: ${quote.ErrorText}` : null,
    quote.DebugText ? `Debug: ${quote.DebugText}` : null
].filter((line): line is string => line !== null);

// the tooltip button's label: why the channel didn't quote, or just its details when it did
export const channelQuoteDetailsLabel = (quote: ChannelQuote): string =>
    quote.Status === 'Ok' ? `Details from ${quote.SalesChannel}` : `Why ${quote.SalesChannel} didn't quote`;

export const remarketedSubmissionLink = (row: RemarketSearchRow): string => `/quote/quote?id=${encodeURIComponent(row.RemarketSubmissionId ?? '')}`;

export const originalAccountLink = (row: RemarketSearchRow): string => `/account?id=${encodeURIComponent(row.SourceAccountId)}`;

const dateOnly = (value?: string | null): string => (value ? value.substring(0, 10) : '');

const CSV_COLUMNS: { header: string; value: (row: RemarketSearchRow) => string | null | undefined }[] = [
    { header: 'Remarketed Submission', value: row => row.RemarketSubmissionId },
    { header: 'Original Submission', value: row => row.SourceSubmissionId },
    { header: 'Original Account', value: row => row.SourceAccountId },
    { header: 'Policy Number', value: row => row.SourcePolicyNumber },
    { header: 'Policy Id', value: row => row.SourcePolicyId },
    { header: 'Current Term Total', value: row => (row.SourceTermTotal != null ? String(row.SourceTermTotal) : '') },
    { header: 'Insured', value: row => row.InsuredName },
    { header: 'Risk Address', value: row => row.RiskAddress },
    { header: 'Effective Date', value: row => dateOnly(row.EffectiveDate) },
    { header: 'Expiration Date', value: row => dateOnly(row.ExpirationDate) },
    { header: 'Agent', value: row => row.AgentName },
    { header: 'Agent Email', value: row => row.AgentEmail },
    { header: 'Sales Channels', value: row => row.AllowedSalesChannels?.join('; ') },
    { header: 'Remarketed At', value: row => row.RemarketedAt },
    { header: 'Quote Requested At', value: row => row.QuoteRequestedAt },
    { header: 'Quote Channel', value: row => row.QuoteSalesChannel },
    { header: 'Quote Total', value: row => (row.QuoteTotal != null ? String(row.QuoteTotal) : '') },
    {
        header: 'Channel Quotes',
        value: row => (row.ChannelQuotes ?? [])
            .map(q => [channelQuoteLabel(q), ...channelQuoteDetails(q)].join(' - '))
            .join('; ')
    },
    { header: 'Status', value: row => releaseStatusLabel(row) },
    { header: 'Released At', value: row => row.ReleasedAt },
    { header: 'Released By', value: row => row.ReleasedBy },
    { header: 'Voided At', value: row => row.VoidedAt },
    { header: 'Voided By', value: row => row.VoidedBy }
];

// Spreadsheets run a cell starting with these as a formula, so a value like "=HYPERLINK(...)" in an insured name could execute
// when the export is opened; prefixing an apostrophe keeps it as text.
const FORMULA_START = /^[=+\-@\t\r]/;

const csvCell = (value: string | null | undefined): string => {
    let text = value ?? '';
    if (FORMULA_START.test(text)) {
        text = `'${text}`;
    }
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const toCsv = (rows: RemarketSearchRow[]): string => {
    const lines = [CSV_COLUMNS.map(c => csvCell(c.header)).join(',')];
    rows.forEach(row => lines.push(CSV_COLUMNS.map(c => csvCell(c.value(row))).join(',')));
    return lines.join('\r\n');
};
